import { Injectable } from '@angular/core';
import { AwsRum, AwsRumConfig } from 'aws-rum-web';
import { environment } from '../../../environments/environment';
import { TokenPropertyService } from './token-property.service';

@Injectable({
  providedIn: 'root'
})
export class CloudWatchRumService {
  private awsRum: AwsRum | undefined;
  private sessionStartTime: number | null = null;
  private rumSessionId: string | null = null;

  constructor(private tokenPropertyService: TokenPropertyService) {}

  /**
   * Clears existing AWS RUM session cookies to force a fresh session
   * Call this before initializeRum() if you need to ensure a clean slate
   */
  clearRumSession(): void {
    try {
      // AWS RUM stores session data in cookies with prefix 'cwr_'
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const trimmedCookie = cookie.trim();
        const cookieName = trimmedCookie.split('=')[0];
        
        // Clear all AWS RUM cookies (cwr_ prefix)
        if (cookieName.startsWith('cwr_')) {
          // Delete cookie by setting expiration to past date
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          // Also try with domain
          const domain = window.location.hostname;
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
        }
      }
      
      // Reset session tracking variables
      this.sessionStartTime = null;
      this.rumSessionId = null;
      
      console.log('AWS RUM session cookies cleared');
    } catch (error) {
      console.error('Error clearing RUM session:', error);
    }
  }

  initializeRum() {
    try {
      const userId = this.tokenPropertyService.getPropertyFromToken('custom:userid', 'id_token') || 'anonymous';

      const config: AwsRumConfig = {
        sessionSampleRate: 1,
        identityPoolId: environment.identityPoolId,
        endpoint: "https://dataplane.rum.us-east-1.amazonaws.com",
        telemetries: ["performance", "errors", "http"],
        allowCookies: true,
        enableXRay: false,
        sessionAttributes: {
          userName: userId,
        }
      };

      const APPLICATION_ID: string = environment.rumApplicationId;
      const APPLICATION_VERSION: string = '1.0.0';
      const APPLICATION_REGION: string = 'us-east-1';

      this.awsRum = new AwsRum(
        APPLICATION_ID,
        APPLICATION_VERSION,
        APPLICATION_REGION,
        config
      );

      // Capture RUM session ID after initialization
      this.captureRumSessionId();
    } catch (error) {
      // Ignore errors thrown during RUM web client initialization
      console.error('Failed to initialize CloudWatch RUM', error);
    }
  }

  /**
   * Captures the RUM session ID from AWS RUM
   * The session ID is typically stored in cookies or localStorage by the RUM SDK
   */
  private captureRumSessionId(): void {
    try {
      // AWS RUM stores session information in cookies with prefix 'cwr_'
      // Try to extract session ID from cookies
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const trimmedCookie = cookie.trim();
        if (trimmedCookie.startsWith('cwr_s=')) {
          // Extract session ID from cookie
          const sessionData = trimmedCookie.substring(6); // Remove 'cwr_s='
          try {
            const parsed = JSON.parse(decodeURIComponent(sessionData));
            this.rumSessionId = parsed.sessionId || parsed.id || sessionData;
          } catch {
            this.rumSessionId = sessionData;
          }
          break;
        }
      }

      // Fallback: generate a session ID if we couldn't extract it
      if (!this.rumSessionId) {
        this.rumSessionId = `rum-session-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
      }
    } catch (error) {
      console.error('Error capturing RUM session ID:', error);
      // Generate fallback session ID
      this.rumSessionId = `rum-session-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    }
  }

  /**
   * Records a login event with session start time and RUM session ID
   */
  recordLoginEvent(): void {
    try {
      this.sessionStartTime = Date.now();
      
      // Ensure we have a session ID
      if (!this.rumSessionId) {
        this.captureRumSessionId();
      }

      const idToken = sessionStorage.getItem('id_token');
      let userId = 'anonymous';
      let clientCode = 'unknown';

      if (idToken) {
        const base64Url = idToken.split('.')[1];
        const base64 = base64Url.replace('-', '+').replace('_', '/');
        const tokenDetails = JSON.parse(atob(base64));
        userId = tokenDetails['custom:userid'] || tokenDetails.sub || 'anonymous';
        clientCode = tokenDetails['custom:clientcode'] || 'unknown';
      }

      const loginData = {
        eventType: 'user_login',
        userId,
        clientCode,
        rumSessionId: this.rumSessionId,
        sessionStartTime: new Date(this.sessionStartTime).toISOString(),
        timestamp: new Date().toISOString()
      };

      this.awsRum?.recordEvent('user_login', loginData);
    } catch (error) {
      console.error('Error recording login event:', error);
    }
  }

  /**
   * Records a logout event with session duration and RUM session ID
   * @param isAutoLogout Whether this is an automatic logout (session timeout)
   * @param logoutSource The source/trigger of the logout (e.g., 'timeout_modal', 'manual_logoff', 'direct_navigation')
   */
  recordLogoutEvent(isAutoLogout: boolean = false, logoutSource: string = 'unknown'): void {
    try {
      const sessionEndTime = Date.now();
      const sessionDuration = this.sessionStartTime 
        ? sessionEndTime - this.sessionStartTime 
        : 0;

      const idToken = sessionStorage.getItem('id_token');
      let userId = 'anonymous';
      let clientCode = 'unknown';

      if (idToken) {
        const base64Url = idToken.split('.')[1];
        const base64 = base64Url.replace('-', '+').replace('_', '/');
        const tokenDetails = JSON.parse(atob(base64));
        userId = tokenDetails['custom:userid'] || tokenDetails.sub || 'anonymous';
        clientCode = tokenDetails['custom:clientcode'] || 'unknown';
      }

      const logoutData = {
        eventType: isAutoLogout ? 'user_auto_logout' : 'user_logout',
        userId,
        clientCode,
        rumSessionId: this.rumSessionId,
        sessionStartTime: this.sessionStartTime ? new Date(this.sessionStartTime).toISOString() : null,
        sessionEndTime: new Date(sessionEndTime).toISOString(),
        sessionDurationMs: sessionDuration,
        sessionDurationMinutes: Math.round(sessionDuration / 60000),
        isAutoLogout,
        logoutSource,
        timestamp: new Date().toISOString()
      };

      this.awsRum?.recordEvent(isAutoLogout ? 'user_auto_logout' : 'user_logout', logoutData);

      // Reset session tracking
      this.sessionStartTime = null;
      this.rumSessionId = null;
    } catch (error) {
      console.error('Error recording logout event:', error);
    }
  }

  /**
   * Gets the current RUM session ID
   * @returns The RUM session ID or null if not available
   */
  getRumSessionId(): string | null {
    return this.rumSessionId;
  }

  /**
   * Gets the session duration in milliseconds
   * @returns The session duration or 0 if session hasn't started
   */
  getSessionDuration(): number {
    if (!this.sessionStartTime) {
      return 0;
    }
    return Date.now() - this.sessionStartTime;
  }

  // Method to add custom events
  addCustomEvent(name: string, data: any) {
    const userId = this.tokenPropertyService.getPropertyFromToken('custom:userid', 'id_token') || 'anonymous';
    data.userId = userId;
    this.awsRum?.recordEvent(name, data);
  }

  // Proxy method for recording page views
  recordPageView(pageName: string, metadata?: any) {
    this.awsRum?.recordPageView({
      pageId: pageName,
      pageAttributes: metadata || {}
    });
  }

  // Method to clean up RUM resources before logout
  cleanup(logoutSource: string = 'manual_logoff') {
    try {
      // Record logout event before cleanup
      this.recordLogoutEvent(false, logoutSource);
      
      // The AWS RUM SDK doesn't have an explicit cleanup method,
      // but we can disable it by setting it to undefined
      this.awsRum = undefined;
    } catch (error) {
      console.error('Error during CloudWatch RUM cleanup:', error);
    }
  }
}